import React, { useEffect, useRef, useState } from "react";
import cn from "classnames";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import OutsideClickHandler from "react-outside-click-handler";
import Icon from "../../../components/Icon";
import { getUsers } from "../../../api/user.service";
import { getDecks } from "../../../api/decks.service";
import { getCards } from "../../../api/cards.service";
import { getTexts } from "../../../api/texts.service";
import { removeDiacritics } from "../../../utils/removeDiacritics";
import styles from "./GlobalSearch.module.sass";

const norm = (s) => removeDiacritics(String(s || "").toLowerCase());

const initials = (name = "") =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("") || "?";

const gradients = [
  ["#FFBC99", "#FF8A65"],
  ["#CABDFF", "#8E59FF"],
  ["#B1E5FC", "#2A85FF"],
  ["#B5E4CA", "#83BF6E"],
  ["#FFD88D", "#FFB020"],
  ["#FFA5D8", "#FF6AC2"],
];
const gradFor = (str = "") => {
  let h = 0;
  for (const c of str) h = (h * 31 + c.charCodeAt(0)) & 0xffffffff;
  const [a, b] = gradients[Math.abs(h) % gradients.length];
  return `linear-gradient(135deg, ${a}, ${b})`;
};

const textTitle = (item, lang) => {
  const list = Array.isArray(item?.title) ? item.title : [];
  return (
    list.find((tr) => tr.lang === lang)?.trans ||
    list.find((tr) => tr.lang === "en")?.trans ||
    list[0]?.trans ||
    "—"
  );
};

const listFrom = (resp) => {
  const d = resp?.data;
  const raw = d?.results || d?.data || d;
  return Array.isArray(raw) ? raw : [];
};

/**
 * Búsqueda global del Home: usuarios y palabras/textos por API, decks filtrados
 * en cliente (el search del endpoint admin/deck no funciona y devuelve todo el
 * listado, así que se cachea una vez y se filtra aquí).
 */
const GlobalSearch = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState(null);
  const decksCache = useRef(null);
  const queryIdRef = useRef(0);
  const debounceRef = useRef(null);
  const lang = (i18n.language || "en").slice(0, 2);

  const ensureDecks = async () => {
    if (decksCache.current) return decksCache.current;
    const resp = await getDecks({ take: 1000 });
    decksCache.current = listFrom(resp).map((d) => ({
      _id: d._id,
      title: d.title,
      level: d.level,
    }));
    return decksCache.current;
  };

  const runSearch = async (q) => {
    const id = ++queryIdRef.current;
    setLoading(true);
    const nq = norm(q);
    const [users, decks, cards, texts] = await Promise.all([
      getUsers({ take: 4, search: q }).then(listFrom).catch(() => []),
      ensureDecks()
        .then((all) => all.filter((d) => norm(d.title).includes(nq)).slice(0, 4))
        .catch(() => []),
      getCards({ take: 4, skip: 0, search: q }).then(listFrom).catch(() => []),
      getTexts({ params: { take: 4, search: q } }).then(listFrom).catch(() => []),
    ]);
    if (id !== queryIdRef.current) return; // llegó tarde, hay una búsqueda más nueva
    setGroups([
      {
        key: "users",
        label: t("home.search.users", "Usuarios"),
        items: users.map((u) => ({
          id: u._id,
          kind: "user",
          primary: u.name || u.username || u.email,
          secondary: u.email,
          to: `/users?search=${encodeURIComponent(u.email || u.name || "")}`,
        })),
      },
      {
        key: "decks",
        label: t("home.search.decks", "Decks"),
        items: decks.map((d) => ({
          id: d._id,
          kind: "deck",
          primary: d.title,
          secondary: d.level === "Begginer" ? "Beginner" : d.level || null,
          to: `/deck/edit/${d._id}`,
        })),
      },
      {
        key: "cards",
        label: t("home.search.words", "Palabras"),
        items: cards.map((c) => ({
          id: c._id,
          kind: "card",
          hanzi: c.meaning?.character,
          primary: c.meaning?.character || `#${c.id}`,
          secondary: c.meaning?.pinyin,
          to: `/card/edit/${c._id}`,
        })),
      },
      {
        key: "texts",
        label: t("home.search.texts", "Textos"),
        items: texts.map((x) => ({
          id: x._id,
          kind: "text",
          primary: textTitle(x, lang),
          secondary: x.level ? `HSK ${x.level}` : null,
          to: `/texts/edit/${x._id}`,
        })),
      },
    ]);
    setLoading(false);
  };

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (query.trim().length < 2) {
      setGroups(null);
      setLoading(false);
      return;
    }
    debounceRef.current = setTimeout(() => runSearch(query.trim()), 300);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const go = (to) => {
    setOpen(false);
    setQuery("");
    setGroups(null);
    history.push(to);
  };

  const firstResult = groups?.flatMap((g) => g.items)[0];
  const total = groups ? groups.reduce((s, g) => s + g.items.length, 0) : 0;
  const showPanel = open && query.trim().length >= 2;

  return (
    <OutsideClickHandler onOutsideClick={() => setOpen(false)}>
      <div className={styles.wrapper}>
        <div className={styles.search}>
          <Icon name="search" size="20" />
          <input
            className={styles.input}
            placeholder={t(
              "home.search.placeholder",
              "Buscar usuario, deck, palabra o texto…"
            )}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setOpen(false);
              if (e.key === "Enter" && firstResult) go(firstResult.to);
            }}
          />
          {loading ? (
            <span className={styles.spinner} />
          ) : (
            <span className={styles.shortcut}>⌘F</span>
          )}
        </div>

        {showPanel && (
          <div className={styles.panel}>
            {groups === null && (
              <div className={styles.state}>{t("home.search.searching", "Buscando…")}</div>
            )}
            {groups !== null && total === 0 && !loading && (
              <div className={styles.state}>
                {t("home.search.noResults", "Sin resultados para")} “{query.trim()}”
              </div>
            )}
            {groups
              ?.filter((g) => g.items.length > 0)
              .map((g) => (
                <div key={g.key} className={styles.group}>
                  <div className={styles.groupLabel}>{g.label}</div>
                  {g.items.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className={styles.row}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        go(item.to);
                      }}
                    >
                      {item.kind === "user" ? (
                        <span
                          className={styles.rowAvatar}
                          style={{ background: gradFor(item.primary) }}
                        >
                          {initials(item.primary)}
                        </span>
                      ) : item.kind === "card" ? (
                        <span className={cn(styles.rowAvatar, styles.rowHanzi)}>
                          {String(item.hanzi || "字").slice(0, 2)}
                        </span>
                      ) : (
                        <span className={cn(styles.rowAvatar, styles.rowEmoji)}>
                          {item.kind === "deck" ? "📘" : "📖"}
                        </span>
                      )}
                      <span className={styles.rowBody}>
                        <span className={styles.rowPrimary}>{item.primary}</span>
                        {item.secondary && (
                          <span className={styles.rowSecondary}>{item.secondary}</span>
                        )}
                      </span>
                      <Icon name="arrow-next" size="16" />
                    </button>
                  ))}
                </div>
              ))}
          </div>
        )}
      </div>
    </OutsideClickHandler>
  );
};

export default GlobalSearch;
